import styled from '@emotion/styled';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import { useTranslation } from 'react-i18next';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { MODIFIED_MAX_BREAKPOINT } from '../general/constants/_breakpoints';
import { SECONDARY_COLOR, PRODUCT_CARD_DARK_BLUE } from '../general/constants/_colors';
import { ChatPopupStandalone } from './ChatPopup';

const ChatButton = ({ onClick, iconMode, ...props }) => {
  const { t } = useTranslation();

  return (
    <ChatButtonWrapper iconMode={iconMode} {...props}>
      <Button variant="outlined" onClick={onClick} data-testid="chat-button">
        <ChatBubbleOutlineIcon fontSize="inherit" />
        <span>{t('Live Chat')}</span>
      </Button>
    </ChatButtonWrapper>
  );
};

export const SupportButton = styled.div`
  color: ${SECONDARY_COLOR};

  button {
    color: #1f2647;
    border: 1px solid ${PRODUCT_CARD_DARK_BLUE};
    border-radius: 800px;
    font-family: Roobert, sans-serif;
    padding: 8px 15px;
    font-size: 16px;

    &:hover {
      border: 1px solid ${PRODUCT_CARD_DARK_BLUE};
    }

    @media only screen and (max-width: ${MODIFIED_MAX_BREAKPOINT}) {
      width: 100%;

      ${({ iconMode }) => {
        return iconMode
          ? `
            border: none!important;
            width: auto;
            `
          : '';
      }}

      span:last-child {
        ${({ iconMode }) => {
          return iconMode
            ? `
            display: none
            `
            : '';
        }}
      }
    }
  }

  span {
    text-transform: none;
  }

  svg {
    margin-right: 10px;

    @media only screen and (max-width: ${MODIFIED_MAX_BREAKPOINT}) {
      ${({ iconMode }) => {
        return iconMode
          ? `
            margin-right: -15px;
            `
          : '';
      }}
    }
  }

  path {
    stroke: ${PRODUCT_CARD_DARK_BLUE};
  }

  @media only screen and (max-width: ${MODIFIED_MAX_BREAKPOINT}) {
    ${({ iconMode }) => {
      return !iconMode
        ? `
            flex-basis: 48%;
            `
        : '';
    }}
  }
`;

const ChatButtonWrapper = styled(SupportButton)`
  min-width: 130px;
  @media only screen and (max-width: ${MODIFIED_MAX_BREAKPOINT}) {
    min-width: unset;
  }

  ${({ iconMode }) => {
    return iconMode
      ? `
           z-index: 23;
           margin-top: 7px;
           margin-right: 5px;
            `
      : '';
  }}

  svg {
    font-size: 20px;

    @media only screen and (max-width: ${MODIFIED_MAX_BREAKPOINT}) {
      ${({ iconMode }) => {
        return iconMode
          ? `
            font-size: 26px;
            `
          : '';
      }}
    }
  }
`;

ChatButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  iconMode: PropTypes.bool,
};

ChatButton.defaultProps = {
  iconMode: false,
};

const ChatControlButtonWrapper = ({ children, open, setOpen }) => {
  return (
    <>
      {children}
      {open && (
        <ChatPopupStandalone
          isOpen={open}
          toggleChatPopup={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
};

ChatControlButtonWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

const ExternalWrapper = styled.div``;

export const ExternalChatButton = ({ children, onClick }) => {
  const [open, setOpen] = useState(false);

  const onButtonClick = () => {
    setOpen(true);
    onClick();
  };

  return (
    <ChatControlButtonWrapper open={open} setOpen={setOpen}>
      <ExternalWrapper onClick={onButtonClick}>{children}</ExternalWrapper>
    </ChatControlButtonWrapper>
  );
};

ExternalChatButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
};

ExternalChatButton.defaultProps = {
  onClick: () => {},
};

export const ChatControlButton = ({ onClick, iconMode }) => {
  const [open, setOpen] = useState(false);

  return (
    <ChatControlButtonWrapper open={open} setOpen={setOpen}>
      <ChatButton
        onClick={() => {
          setOpen(true);
          onClick();
        }}
        iconMode={iconMode}
      />
    </ChatControlButtonWrapper>
  );
};

ChatControlButton.propTypes = {
  onClick: PropTypes.func,
  iconMode: PropTypes.bool,
};

ChatControlButton.defaultProps = {
  onClick: () => {},
  iconMode: true,
};

export default ChatButton;
